import React, { useEffect, useMemo, useState } from 'react'
import { createPortal } from 'react-dom'
import gotchiIcon from '@assets/images/characters/gotchi.png'
import './GotchiWaddleCelebration.scss'

const MAX_MS = 1800

export type FireworkOrigin = {
  x: number
  y: number
}

type Floater = {
  id: number
  sizePx: number
  durationMs: number
  delayMs: number
  arc1X: number
  arc1Y: number
  apexX: number
  apexY: number
  arc2X: number
  arc2Y: number
  fallX: number
  fallY: number
  spinDeg: number
}

type Props = {
  active: boolean
  /** Viewport click point (clientX/clientY). Falls back to center. */
  origin?: FireworkOrigin | null
  onDone?: () => void
}

function prefersReducedMotion() {
  return typeof window !== 'undefined' && window.matchMedia('(prefers-reduced-motion: reduce)').matches
}

type Ring = {
  count: number
  radiusMin: number
  radiusMax: number
  sizeMin: number
  sizeMax: number
  durationMin: number
  durationMax: number
  delayMax: number
  fallScale: number
}

const RINGS: Ring[] = [
  {
    count: 10,
    radiusMin: 70,
    radiusMax: 120,
    sizeMin: 16,
    sizeMax: 28,
    durationMin: 900,
    durationMax: 1200,
    delayMax: 40,
    fallScale: 1.55,
  },
  {
    count: 12,
    radiusMin: 140,
    radiusMax: 220,
    sizeMin: 24,
    sizeMax: 42,
    durationMin: 1100,
    durationMax: 1450,
    delayMax: 90,
    fallScale: 1.85,
  },
  {
    count: 14,
    radiusMin: 230,
    radiusMax: 340,
    sizeMin: 32,
    sizeMax: 58,
    durationMin: 1300,
    durationMax: 1750,
    delayMax: 140,
    fallScale: 2.15,
  },
]

/**
 * Upper semicircle only: left → top → right (screen Y+ is down, so sin < 0 = up).
 * Angles from π (left) to 2π (right) through 3π/2 (up).
 */
function buildFloaters(): Floater[] {
  const floaters: Floater[] = []
  let id = 0

  for (const ring of RINGS) {
    for (let i = 0; i < ring.count; i += 1) {
      const t = ring.count === 1 ? 0.5 : i / (ring.count - 1)
      const angle = Math.PI + Math.PI * t + (Math.random() - 0.5) * 0.12
      const radius = ring.radiusMin + Math.random() * (ring.radiusMax - ring.radiusMin)
      const cos = Math.cos(angle)
      const sin = Math.sin(angle)

      const apexX = cos * radius
      const apexY = sin * radius
      const tangent = angle + Math.PI / 2
      const curve = (0.12 + Math.random() * 0.18) * radius * (Math.random() < 0.5 ? -1 : 1)
      const arc1X = cos * radius * 0.42 + Math.cos(tangent) * curve * 0.35
      const arc1Y = sin * radius * 0.42 + Math.sin(tangent) * curve * 0.35
      // Fall farther out/down from apex, but burst never aimed below origin
      const arc2X = cos * radius * ring.fallScale * 0.72 + Math.cos(tangent) * curve
      const arc2Y = apexY + Math.abs(radius) * 0.55 + 20
      const fallX = cos * radius * ring.fallScale + (Math.random() - 0.5) * 40
      const fallY = 90 + Math.random() * 50

      floaters.push({
        id: id++,
        sizePx: ring.sizeMin + Math.floor(Math.random() * (ring.sizeMax - ring.sizeMin + 1)),
        durationMs:
          ring.durationMin + Math.floor(Math.random() * (ring.durationMax - ring.durationMin + 1)),
        delayMs: Math.floor(Math.random() * ring.delayMax),
        arc1X,
        arc1Y,
        apexX,
        apexY,
        arc2X,
        arc2Y,
        fallX,
        fallY,
        spinDeg: 200 + Math.floor(Math.random() * 640) * (Math.random() < 0.5 ? -1 : 1),
      })
    }
  }

  return floaters
}

/**
 * “Oui” firework: bursts left / up / right from click point (not below).
 */
export function GotchiWaddleCelebration({ active, origin, onDone }: Props) {
  const [visible, setVisible] = useState(false)
  const [floaters, setFloaters] = useState<Floater[]>([])

  useEffect(() => {
    if (!active) {
      setVisible(false)
      return
    }
    if (prefersReducedMotion()) {
      onDone?.()
      return
    }
    setFloaters(buildFloaters())
    setVisible(true)
    const t = window.setTimeout(() => {
      setVisible(false)
      onDone?.()
    }, MAX_MS)
    return () => window.clearTimeout(t)
  }, [active, onDone])

  const root = useMemo(() => {
    if (typeof document === 'undefined') return null
    return document.querySelector('.AppLayout') ?? document.body
  }, [visible])

  const originStyle = useMemo(() => {
    if (origin && Number.isFinite(origin.x) && Number.isFinite(origin.y)) {
      return { left: origin.x, top: origin.y }
    }
    return { left: '50%', top: '55%' }
  }, [origin])

  if (!visible || !root) return null

  return createPortal(
    <div className="GotchiWaddleCelebration" aria-hidden>
      <div className="GotchiWaddleCelebration-origin" style={originStyle}>
        {floaters.map((f) => (
          <img
            key={f.id}
            src={gotchiIcon}
            alt=""
            className="GotchiWaddleCelebration-gotchi"
            style={
              {
                width: f.sizePx,
                height: f.sizePx,
                '--gw-duration': `${f.durationMs}ms`,
                '--gw-delay': `${f.delayMs}ms`,
                '--gw-arc1-x': `${f.arc1X}px`,
                '--gw-arc1-y': `${f.arc1Y}px`,
                '--gw-apex-x': `${f.apexX}px`,
                '--gw-apex-y': `${f.apexY}px`,
                '--gw-arc2-x': `${f.arc2X}px`,
                '--gw-arc2-y': `${f.arc2Y}px`,
                '--gw-fall-x': `${f.fallX}px`,
                '--gw-fall-y': `${f.fallY}vh`,
                '--gw-spin': `${f.spinDeg}deg`,
              } as React.CSSProperties
            }
          />
        ))}
      </div>
    </div>,
    root,
  )
}

export default GotchiWaddleCelebration
